import { CopyButton } from "@components/CopyButton";
import { Error } from "@components/Error";
import { Loader } from "@components/Loader";
import { CreatedBy } from "@components/backoffice/ChangedBy";
import { DetailsHeader } from "@components/backoffice/DetailsHeader";
import { EditButton } from "@components/backoffice/buttons/EditButton";
import { ScrollingMenuItem } from "@components/backoffice/buttons/ScrollingMenu";
import { AcquisitionOrderIcon } from "@components/backoffice/icons";
import { AcquisitionRoutes } from "@routes/urls";
import PropTypes from "prop-types";
import React from "react";
import {
  Container,
  Divider,
  Grid,
  Header,
  Label,
  Ref,
  Segment,
  Sticky,
} from "semantic-ui-react";
import { OrderInformation } from "./OrderInformation";
import { OrderStatistics } from "./OrderStatistics";
import { OrderSteps } from "./OrderSteps";

class OrderHeader extends React.Component {
  renderStatus(status) {
    switch (status) {
      case "CANCELLED":
        return <Label color="grey">Cancelled</Label>;
      case "PENDING":
        return <Label color="yellow">Pending</Label>;
      case "ORDERED":
        return <Label color="yellow">Ordered</Label>;
      case "RECEIVED":
        return <Label color="green">Received</Label>;
      default:
        throw new Error(`Unknown status: ${status}`);
    }
  }

  render() {
    const { data } = this.props;
    const pid = data.metadata.pid;
    const recordInfo = (
      <>
        <label>Record</label> #{pid} <CopyButton text={pid} />
        {data.metadata.created_by && (
          <>
            <br />
            <label>Created by</label> <CreatedBy metadata={data.metadata} />
          </>
        )}
        <br />
        {data.metadata.order_date && (
          <>
            <label>Order date</label> {data.metadata.order_date}
          </>
        )}
      </>
    );
    return (
      <DetailsHeader
        title={
          <>
            Acquisition Record #{data.metadata.pid}{" "}
            {this.renderStatus(data.metadata.status)}
          </>
        }
        pid={data.metadata.pid}
        icon={<AcquisitionOrderIcon />}
        recordInfo={recordInfo}
      />
    );
  }
}

OrderHeader.propTypes = {
  data: PropTypes.object.isRequired,
};

class ActionMenu extends React.Component {
  render() {
    const { data: order } = this.props;

    return (
      <div className="bo-action-menu">
        {order && (
          <EditButton fluid to={AcquisitionRoutes.orderEditFor(order.pid)} />
        )}
        <Divider horizontal>Navigation</Divider>
        <ScrollingMenuItem elementId="order-info" label="Records information" />
      </div>
    );
  }
}

ActionMenu.propTypes = {
  data: PropTypes.object.isRequired,
};

class OrderPanels extends React.Component {
  render() {
    const { data } = this.props;
    return (
      <>
        <Header as="h3" attached="top">
          Records Information
        </Header>
        <Segment attached className="bo-metadata-segment">
          <OrderInformation order={data} />
        </Segment>
      </>
    );
  }
}

OrderPanels.propTypes = {
  data: PropTypes.object.isRequired,
};

// Enhanced OrderImages component with better image handling
const OrderImages = ({ imageFilenames }) => {
  // Handle both array of filenames and empty/null cases
  const images = Array.isArray(imageFilenames) ? imageFilenames : [];

  if (images.length === 0) return null;

  return (
    <Segment
      attached
      className="bo-images-segment"
      style={{ marginTop: "1em" }}
    >
      <Header as="h3">Uploaded Damage Images ({images.length})</Header>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
          gap: "15px",
          marginTop: "10px",
        }}
      >
        {images.map((filename, index) => (
          <div
            key={`${filename}-${index}`}
            style={{
              border: "1px solid #ddd",
              borderRadius: "4px",
              overflow: "hidden",
              backgroundColor: "#f9f9f9",
            }}
          >
            <img
              src={filename}
              alt={`Damage evidence ${index + 1}`}
              style={{
                width: "100%",
                height: "150px",
                objectFit: "cover",
                display: "block",
              }}
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
            <div
              style={{
                display: "none",
                height: "150px",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#f0f0f0",
                color: "#666",
                fontSize: "12px",
                padding: "10px",
                textAlign: "center",
              }}
            >
              <div>
                <div>Image failed to load</div>
                <div
                  style={{
                    marginTop: "5px",
                    fontSize: "10px",
                    wordBreak: "break-all",
                  }}
                >
                  {filename}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Segment>
  );
};

OrderImages.propTypes = {
  imageFilenames: PropTypes.arrayOf(PropTypes.string),
};

OrderImages.defaultProps = {
  imageFilenames: [],
};

export default class OrderDetails extends React.Component {
  constructor(props) {
    super(props);

    this.headerRef = React.createRef();
    this.menuRef = React.createRef();
  }

  componentDidMount() {
    const { fetchOrderDetails, match } = this.props;
    fetchOrderDetails(match.params.orderPid);
  }

  componentDidUpdate(prevProps) {
    const { fetchOrderDetails, match } = this.props;
    const orderPid = match.params.orderPid;

    const samePidFromRouter = prevProps.match.params.orderPid === orderPid;
    if (!samePidFromRouter) {
      fetchOrderDetails(orderPid);
    }
  }

  render() {
    const { isLoading, error, data } = this.props;
    const metadata = data.metadata || {};
    return (
      <div ref={this.headerRef}>
        <Container fluid>
          <Loader isLoading={isLoading}>
            <Error error={error}>
              <Sticky
                context={this.headerRef}
                className="solid-background"
                offset={120}
              >
                <Container fluid className="spaced">
                  <OrderHeader data={data} />
                </Container>
                <Divider />
              </Sticky>

              <Container fluid>
                <Ref innerRef={this.menuRef}>
                  <Grid columns={2}>
                    <Grid.Column width={13}>
                      <Container className="spaced">
                        <Container className="spaced">
                          <OrderStatistics order={metadata} />
                          <br />
                          <OrderSteps order={metadata} />

                          {/* Added image display */}
                          <OrderImages
                            imageFilenames={metadata.image_filenames}
                          />
                        </Container>
                        <OrderPanels data={data} />
                      </Container>
                    </Grid.Column>
                    <Grid.Column width={3}>
                      <Sticky context={this.menuRef} offset={150}>
                        <ActionMenu data={metadata} offset={-150} />
                      </Sticky>
                    </Grid.Column>
                  </Grid>
                </Ref>
              </Container>
            </Error>
          </Loader>
        </Container>
      </div>
    );
  }
}

OrderDetails.propTypes = {
  data: PropTypes.object.isRequired,
  fetchOrderDetails: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  error: PropTypes.object,
  match: PropTypes.shape({
    params: PropTypes.shape({
      orderPid: PropTypes.string,
    }),
  }).isRequired,
};

OrderDetails.defaultProps = {
  isLoading: false,
  error: null,
};
